import React, { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { inventoryAPI } from '../../services';
import { toast } from 'react-toastify';
import { baseToPackageQty, packageQtyToBase, inputUnitLabel } from '../../utils/inventoryUnit';

const fmtN = (n) => Number(n ?? 0).toLocaleString('vi-VN', { maximumFractionDigits: 3 });

/**
 * #232210 — chỉ Admin trung tâm sửa được ngưỡng tối thiểu RIÊNG theo chi nhánh
 * (`BranchStock.min_stock_override`), việc gọi component này phải tự kiểm `user.role === 'ADMIN'`
 * trước (BE cũng chặn lại bằng `RolesGuard`, không tin FE).
 */
export default function BranchMinStockOverrideModal({ show, onHide, branchId, branchName, stock, onSuccess }) {
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);

  const material = stock?.material;
  const unitLabel = inputUnitLabel(material);
  const defaultThresholdPkg = baseToPackageQty(material?.min_stock_threshold, material);

  useEffect(() => {
    if (show && stock) {
      setValue(
        stock.min_stock_override != null
          ? String(baseToPackageQty(stock.min_stock_override, material))
          : '',
      );
    }
  }, [show, stock, material]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (value !== '' && Number(value) < 0) {
      toast.error('Ngưỡng tối thiểu không được âm');
      return;
    }
    try {
      setLoading(true);
      const baseValue = value === '' ? null : packageQtyToBase(Number(value), material);
      await inventoryAPI.setBranchMinStockOverride(branchId, material.id, baseValue);
      toast.success(
        baseValue === null
          ? 'Đã xoá ngưỡng riêng — quay về mức chung'
          : 'Đã lưu ngưỡng tối thiểu riêng cho chi nhánh này',
      );
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err?.message || 'Không thể lưu ngưỡng tối thiểu');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = async () => {
    try {
      setLoading(true);
      await inventoryAPI.setBranchMinStockOverride(branchId, material.id, null);
      toast.success('Đã xoá ngưỡng riêng — quay về mức chung');
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err?.message || 'Không thể xoá ngưỡng riêng');
    } finally {
      setLoading(false);
    }
  };

  if (!stock || !material) return null;

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Ngưỡng tối thiểu riêng — {material.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            Chi nhánh: <strong>{branchName}</strong>. Để trống ô bên dưới = dùng mức CHUNG cho mọi
            chi nhánh chưa có override riêng.
          </p>
          <Form.Group className="mb-2">
            <Form.Label>Mức chung hiện tại</Form.Label>
            <div className="text-muted">{fmtN(defaultThresholdPkg)} {unitLabel}</div>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Ngưỡng riêng cho chi nhánh này ({unitLabel})</Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="0.001"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={`Để trống = dùng mức chung (${fmtN(defaultThresholdPkg)} ${unitLabel})`}
            />
          </Form.Group>
          {stock.min_stock_override != null && (
            <Form.Text className="text-info">
              Chi nhánh này đang dùng mức RIÊNG: {fmtN(baseToPackageQty(stock.min_stock_override, material))} {unitLabel}
            </Form.Text>
          )}
        </Modal.Body>
        <Modal.Footer>
          {stock.min_stock_override != null && (
            <Button variant="outline-danger" onClick={handleClear} disabled={loading} className="me-auto">
              Xoá override, dùng mức chung
            </Button>
          )}
          <Button variant="secondary" onClick={onHide} disabled={loading}>Hủy</Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? 'Đang lưu...' : 'Lưu'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
